import { useContext, useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import { TextField } from '@mui/material';
import { Form } from 'react-bootstrap';
import Button from '@mui/material/Button';
import { AuthContext } from "../context/AuthContext";
import axios from 'axios';
import { showSweetAlert } from "../components/SweetAlert";

const SwitchAccount = ({open, onClose, preSelectProvider, prefilledKeys, onKeysUpdated}) => {
    console.log('[SwitchAccount] Component rendered with props:', { open, preSelectProvider, prefilledKeys });

    const {createNewIntegrations, fetchUserData, integrationsParams, aiSettings, setUserData} = useContext(AuthContext)
    const [searchQuery, setSearchQuery] = useState(''); 
    const [activeMenu, setActiveMenu] = useState("option2");
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [loading, setLoading] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [errors, setErrors] = useState({ api: '', secret: '' });

    // Handle preSelectProvider prop - directly open Crypto.com dialog
    useEffect(() => {
        if (preSelectProvider === 'Crypto.com' && open && !selectedWidget) {
            console.log('[SwitchAccount] preSelectProvider is Crypto.com, setting selectedWidget');
            setSelectedWidget({
                label: 'Crypto.com',
                src: 'crypto.svg',
                key: 'cryptocom'
            });
        } else if (!open && selectedWidget && selectedWidget.label === 'Crypto.com') {
            // Clear selectedWidget when dialog closes
            console.log('[SwitchAccount] Dialog closed, clearing selectedWidget');
            setSelectedWidget(null);
        }
    }, [preSelectProvider, open, selectedWidget]);

    // Pre-fill keys when dialog opens with prefilledKeys (for invalid keys scenario)
    // Use a ref to track if we've already pre-filled to prevent overwriting user input
    const hasPrefilledRef = useRef(false);
    const previousOpenRef = useRef(false);
    
    useEffect(() => {
        // Only pre-fill when dialog transitions from closed to open with prefilledKeys
        const justOpened = open && !previousOpenRef.current;
        
        if (justOpened && prefilledKeys && preSelectProvider === 'Crypto.com' && !hasPrefilledRef.current) {
            console.log('[SwitchAccount] Pre-filling keys from invalid keys notification');
            setApiKey(prefilledKeys.apiKey || '');
            setSecretKey(prefilledKeys.apiSecret || '');
            hasPrefilledRef.current = true;
            
            // Ensure dialog is opened for Crypto.com
            if (!selectedWidget) {
                setSelectedWidget({
                    label: 'Crypto.com',
                    src: 'crypto.svg',
                    key: 'cryptocom'
                });
            }
        } else if (!open && previousOpenRef.current) {
            // Dialog just closed - clear keys and reset ref
            setApiKey('');
            setSecretKey('');
            hasPrefilledRef.current = false;
        }
        
        previousOpenRef.current = open;
    }, [open, prefilledKeys, preSelectProvider, selectedWidget]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const widgetsByOption = {
        option1: [
            { src: 'traditional_charles.svg', label: 'Charles Schwab', key: 'charlew', height: 3, width: 2 },
            { src: 'merill.svg', label: 'Merill Lynch', key: 'merill', height: 3, width: 2 },
            { src: 'morgan.svg', label: 'Morgan Stanley', key: 'morgan', height: 3, width: 2 },
            { src: 'jp_morgan.svg', label: 'J.P.Morgan', key: 'jpmorgen', height: 3, width: 2 },
            { src: 'td.svg', label: 'TD', key: 'td', height: 3, width: 2 },
            { src: 'wells.svg', label: 'Wells Fargo', key: 'wells', height: 3, width: 2 },
            { src: 'citi.svg', label: 'CITI', key: 'citi', height: 3, width: 2 },
        ],
        option2: [
            { src: 'thirdp_ib.svg', label: 'Interactive Brokers', key: 'ibkr', height: 1, width: 1 },
            { src: 'etrade.svg', label: 'E*Trade', key: 'etrade', height: 1, width: 1 },
            // { src: 'trade_station.svg', label: 'Trade Station', key: 'trade', height: 1, width: 1 },
            // { src: 'webull.svg', label: 'Webull', key: 'webull', height: 1, width: 1 },
            // { src: 'robin_hood.svg', label: 'Robinhood', key: 'robin', height: 1, width: 1 },
            // { src: 'tastytrade.svg', label: 'Tastytrade', key: 'tasty', height: 1, width: 1 },
            // { src: 'bloomberg.svg', label: 'Bloomberg', key: 'bloom', height: 1, width: 1 },
        ],
        option3: [
            // { src: 'crypto_coin.svg', label: 'Coinbase', key: 'coin', height: 1, width: 1 },
            { src: 'crypto.svg', label: 'Crypto.com', key: 'crypto', height: 1, width: 1 },
            // { src: 'binance.svg', label: 'Binance', key: 'binance', height: 1, width: 1 },
            // { src: 'kraken.svg', label: 'Kraken', key: 'kraken', height: 1, width: 1 },
            // { src: 'gemini.svg', label: 'Gemini', key: 'gemini', height: 1, width: 1 },
        ]
    };

    const handleWidgetClick = async(widget) => {
        // setSelectedWidget(widget);
        console.log(widget);
        
        const selectedName = widget?.replace(/\s+/g, '');
        const response = await createNewIntegrations(selectedName,'','','');
        if(response.message === 'Integration added successfully'){
            setLoading(false);
            setApiKey('');
            setSecretKey('');
            showSweetAlert('Success!', `Account Added Successfully`, 'success');
            await fetchUserData();
        }
    };

    const handleCloseSecondDialog = () => {
        // If this is the Crypto.com dialog opened via preSelectProvider, close the main dialog
        if (preSelectProvider === 'Crypto.com') {
            console.log('[SwitchAccount] Closing Crypto.com dialog, calling onClose');
            setSelectedWidget(null);
            setApiKey('');
            setSecretKey('');
            setLoading(false);
            onClose(); // Close the main dialog
        } else {
            // For regular widget selection, just clear the selected widget
            setSelectedWidget(null);
            setApiKey('');
            setSecretKey('');
            setLoading(false);
        }
    };

    const disabledKeys = [
        "charlew", "merill", "morgan", "jpmorgen", "td",
        "wells", "citi", "trade", "webull", "robin", "tasty",
        "bloom", "binance", "kraken", "gemini"
    ];

    const renderContent = () => {
        const filteredWidgets = widgetsByOption[activeMenu]?.filter(widget =>
            widget.label.toLowerCase().includes(searchQuery) // Filter based on search query
        ) || [];
    
        return (
            <div className="content-area" style={{ display: 'flex', flexWrap: 'wrap' }}>
                {filteredWidgets.map((widget, index) => {
                    const isDisabled = disabledKeys.includes(widget.key);

    
                    return (
                        <div 
                            key={index} 
                            className="widget-container" 
                            style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                margin: '15px', 
                                position: 'relative', 
                                width: '140px',
                                opacity: isDisabled ? 0.5 : 1, // Reduce opacity for disabled widgets
                                cursor: isDisabled ? 'not-allowed' : 'pointer' // Change cursor style
                            }} 
                            onClick={!isDisabled ? () => handleWidgetClick(widget.label) : undefined}
                        >
                            <img
                                style={{ width: "80px", cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                src={`${process.env.PUBLIC_URL}/images/switch_acc/${widget.src}`}
                                alt={widget.label}
                                className="customize-icon"
                                role="button"
                            />
                            <div style={{ marginTop: '5px', fontSize: '14px' }}>
                                {widget.label} 
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    
    
    const hendleKeySubmit = async (event) => {
        event.preventDefault();
        const widgetLabel = selectedWidget?.label || (preSelectProvider === 'Crypto.com' ? 'Crypto.com' : '');
        const selectedName = widgetLabel.replace(/\s+/g, '');
        
        if (!apiKey || !secretKey) {
            showSweetAlert('Error', 'Please enter both API Key and Secret Key', 'error');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await createNewIntegrations(selectedName, apiKey, secretKey, '');
            
            // Check for both "added" and "updated" success messages
            if(response && (response.message === 'Integration added successfully' || response.message === 'Integration updated successfully')){
                setLoading(false);
                setApiKey('');
                setSecretKey('');
                handleCloseSecondDialog();
                await fetchUserData();
                showSweetAlert('Success!', 'Crypto.com API keys saved successfully', 'success');
                
                // Clear invalid keys notification if keys were updated
                if (onKeysUpdated && typeof onKeysUpdated === 'function') {
                    onKeysUpdated();
                }
            } else {
                setLoading(false);
                showSweetAlert('Error', 'Failed to save API keys', 'error');
            }
        } catch (error) {
            console.error('Error saving Crypto.com API keys:', error);
            setLoading(false);
            showSweetAlert('Error', 'Failed to save API keys', 'error');
        }
    }

    const handleFirstDialogClose = async () => {
        const token = localStorage.getItem('accessToken');
        const userId = localStorage.getItem('userId');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        setUserData(response.data);
        onClose();
    };
    

    return (
        <>
            {/* Only show "Link account" dialog if preSelectProvider is not set */}
            {!preSelectProvider && (
                <Dialog
                    open={open} onClose={onClose}
                    aria-labelledby="settings-dialog-title"
                    fullWidth
                    PaperProps={{
                        style: {
                            width: '800px',
                            maxWidth: '100%',
                        },
                    }}
                    className="setting-dialog"
                >
                <DialogTitle id="settings-dialog-title" className="d-flex justify-content-between mb-2">
                    Link account
                    <img
                        style={{ height: "22px", width: "20px", cursor: "pointer" }}
                        src={`${process.env.PUBLIC_URL}/images/close.png`}
                        alt="close"
                        onClick={handleFirstDialogClose}
                    />
                </DialogTitle>
                <div className="dialog-body">
                    <div className="switch_acc_sidebar">
                        <Form>
                            <FormControl className="square-form-control-search m-0" sx={{ m: 1, minWidth: 120 }} style={{ textAlign: 'left' }}>
                                <TextField
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={handleSearchChange} // Update search input
                                    fullWidth
                                    margin="normal"
                                    type="text"
                                />
                            </FormControl>
                        </Form>
                        <div onClick={() => setActiveMenu("option1")} className={`${activeMenu === "option1" ? "active" : ""} mt-3`} style={{display: 'none'}}>
                            <p className="mb-0"> Traditional</p>
                        </div>
                        <div onClick={() => setActiveMenu("option2")} className={`${activeMenu === "option2" ? "active" : ""} mt-3`}>
                            <p className="mb-0"> Third-party platforms</p>
                        </div>
                        <div onClick={() => setActiveMenu("option3")} className={`${activeMenu === "option3" ? "active" : ""} mt-3`}>
                            <p className="mb-0"> Crypto exchanges</p>
                        </div>
                    </div>
                    <div className="divider"></div>
                    <div className="content-area scrollbar-custom">
                        {renderContent()}
                    </div>
                </div>
            </Dialog>
            )}

            {/* Show Crypto.com dialog when preSelectProvider is set or selectedWidget is Crypto.com */}
            {((selectedWidget && selectedWidget.label === 'Crypto.com') || (preSelectProvider === 'Crypto.com' && open)) && (
                <Dialog
                    open={open && (!!selectedWidget || preSelectProvider === 'Crypto.com')}
                    onClose={handleCloseSecondDialog}
                    disableEscapeKeyDown={false}
                    aria-labelledby="widget-dialog-title"
                    fullWidth
                    PaperProps={{
                        style: {
                            width: '600px',
                            maxWidth: '100%',
                        },
                    }}
                    className="widget-dialog"
                >
                    <DialogTitle id="widget-dialog-title" className="d-flex justify-content-between mb-2">
                        <div className="d-flex align-items-center">
                            <img style={{ float: 'none', height: "18px", width: "9px" }} src={`${process.env.PUBLIC_URL}/images/left.svg`} alt="left" className="close-icon" role="button" 
                            onClick={handleCloseSecondDialog} />
                            <span style={{marginLeft:"15px"}}>Connect Crypto.com</span>
                        </div>
                        <img
                            style={{ height: "22px", width: "20px", cursor: "pointer" }}
                            src={`${process.env.PUBLIC_URL}/images/close.png`}
                            alt="close"
                            onClick={handleCloseSecondDialog}
                        />
                    </DialogTitle>
                    <DialogContent className="mt-2">
                        <div className="d-flex justify-content-center align-items-center mb-3">
                            <img
                                style={{ width: "50px", display: 'block' }}
                                src={`${process.env.PUBLIC_URL}/images/switch_acc/nodoji.svg`}
                                alt='nodoji'
                            />
                            <img
                                style={{ width: "40px", display: 'block', marginLeft:'30px', marginRight:'30px' }}
                                src={`${process.env.PUBLIC_URL}/images/switch_acc/switch_between.svg`}
                                alt='switch_between'
                            />
                            <img
                                style={{ width: "50px", display: 'block'}}
                                src={`${process.env.PUBLIC_URL}/images/switch_acc/crypto.svg`}
                                alt="Crypto.com"
                            />
                        </div>
                        
                        {/* Instructions Section */}
                        <div style={{ 
                            backgroundColor: '#2a2a2a', 
                            padding: '15px', 
                            borderRadius: '8px', 
                            marginBottom: '20px',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: '#ffffff'
                        }}>
                            <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '16px' }}>
                                How to Create Your API Key:
                            </div>
                            <ol style={{ marginLeft: '20px', marginBottom: '10px', paddingLeft: '10px' }}>
                                <li style={{ marginBottom: '8px' }}>Log in to your <strong>Crypto.com Exchange</strong> account</li>
                                <li style={{ marginBottom: '8px' }}>Click your <strong>Profile</strong> (upper right-hand corner)</li>
                                <li style={{ marginBottom: '8px' }}>Select <strong>Manage Account</strong>, then <strong>API Management</strong></li>
                                <li style={{ marginBottom: '8px' }}>Select <strong>Create a new API key</strong>, enter a description/label, and enter your 2FA code</li>
                                <li style={{ marginBottom: '8px' }}>A public and secret key will be generated. You can enable IP restrictions if needed. All new API Keys are created with default permission of "Can Read". You can then choose to enable trading for the API Key</li>
                                <li style={{ marginBottom: '8px' }}>If you choose <strong>Enable Trading</strong> or <strong>Enable Withdrawal</strong>, it is required to whitelist an IP address</li>
                            </ol>
                            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #555' }}>
                                <strong>Note:</strong> Secret Keys are only visible at the time the API Key is created. If you forget it, you will need to create a new API Key.
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <a 
                                    href="https://help.crypto.com/en/articles/3511424-api?utm_source=chatgpt.com#h_03933ddaa1" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                        color: '#4a9eff', 
                                        textDecoration: 'underline',
                                        fontSize: '14px'
                                    }}
                                >
                                    View full API documentation →
                                </a>
                            </div>
                        </div>

                        <Form onSubmit={hendleKeySubmit}>
                            <div className="mb-2">
                                <span style={{fontSize: "16px"}}>API Key</span>
                            </div>
                            <Form.Group controlId="formBasicEmail" className="control">
                                <FormControl className="square-form-control-update m-0" sx={{ m: 1, minWidth: 120 }}>
                                    <TextField
                                        placeholder="Please enter your Crypto.com API Key"
                                        value={apiKey}
                                        onChange={(e) => setApiKey(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        type="text"
                                    />
                                </FormControl>
                            </Form.Group>
                            {/* {errors.api && <div className="text-danger mt-1">{errors.api}</div>} */}

                            <div className="mt-3">
                                <div className="mb-2">
                                    <span style={{ fontSize: "16px" }}>Secret Key</span>
                                </div>
                                <Form.Group controlId="formBasicEmail" className="control">
                                    <FormControl className="square-form-control-update m-0" sx={{ m: 1, minWidth: 120 }}>
                                        <TextField
                                            placeholder="Please enter your Crypto.com Secret Key"
                                            value={secretKey}
                                            onChange={(e) => setSecretKey(e.target.value)}
                                            fullWidth
                                            margin="normal"
                                            type="text"
                                        />
                                    </FormControl>
                                </Form.Group>
                                {/* {errors.secret && <div className="text-danger mt-1">{errors.secret}</div>} */}
                            </div>
                            
                            <div className="mt-2 row justify-content-center">
                                <Button variant="primary" type="submit" style={{backgroundColor: '#48494C', textTransform: 'none'}} className="mt-5 w-55 p-2 theme-btn-email" disabled={loading}>
                                {loading ? 'Submitting...' : 'Submit'}
                                </Button>
                            </div>
                        </Form>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

export default SwitchAccount;